import type { ReactElement } from 'react';
import { useCallback, useEffect, useState } from 'react';
import type { FanClubTierSummary } from '@fetmode/types';
import { ApiError } from '@fetmode/api-client';
import { useAuth } from '@fetmode/app-core';
import { t } from '@fetmode/i18n';
import { Button } from '@fetmode/ui';

type TierDraft = {
  name: string;
  monthlyCoins: string;
  perks: string;
};

const emptyDraft = (): TierDraft => ({
  name: '',
  monthlyCoins: '200',
  perks: '',
});

export function ModelFanClubTiersCard(): ReactElement {
  const { api } = useAuth();
  const [tiers, setTiers] = useState<FanClubTierSummary[]>([]);
  const [draft, setDraft] = useState<TierDraft>(emptyDraft);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async (): Promise<void> => {
    const result = await api.listMyFanClubTiers();
    setTiers(result.tiers);
  }, [api]);

  useEffect(() => {
    void load()
      .catch(() => {
        setError(t('common.studio.fanClubTiers.loadError'));
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [load]);

  async function handleAdd(): Promise<void> {
    const monthlyCoins = Number.parseInt(draft.monthlyCoins, 10);
    if (!draft.name.trim() || !Number.isFinite(monthlyCoins) || monthlyCoins < 1) {
      setError(t('common.studio.fanClubTiers.invalidItem'));
      return;
    }
    setIsSaving(true);
    setError(null);
    try {
      await api.createFanClubTier({
        name: draft.name.trim(),
        monthlyCoins,
        perks: draft.perks.trim() || null,
      });
      setDraft(emptyDraft());
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('common.studio.fanClubTiers.saveError'));
    } finally {
      setIsSaving(false);
    }
  }

  async function handleToggleEnabled(tier: FanClubTierSummary): Promise<void> {
    setIsSaving(true);
    setError(null);
    try {
      await api.updateFanClubTier(tier.id, { enabled: !tier.enabled });
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('common.studio.fanClubTiers.saveError'));
    } finally {
      setIsSaving(false);
    }
  }

  async function handleDelete(tierId: string): Promise<void> {
    setIsSaving(true);
    setError(null);
    try {
      await api.deleteFanClubTier(tierId);
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('common.studio.fanClubTiers.saveError'));
    } finally {
      setIsSaving(false);
    }
  }

  if (isLoading) {
    return (
      <p style={{ color: 'var(--fm-color-text-muted)' }}>{t('common.studio.fanClubTiers.loading')}</p>
    );
  }

  return (
    <div className="fm-card">
      <h2 style={{ marginTop: 0, fontSize: '1rem' }}>{t('common.studio.fanClubTiers.title')}</h2>
      <p style={{ margin: '0 0 1rem', color: 'var(--fm-color-text-muted)', fontSize: '0.875rem' }}>
        {t('common.studio.fanClubTiers.hint')}
      </p>

      {error ? <p className="fm-error">{error}</p> : null}

      <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 1rem' }}>
        {tiers.map((tier) => (
          <li
            key={tier.id}
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: '0.5rem',
              alignItems: 'center',
              padding: '0.5rem 0',
              borderBottom: '1px solid var(--fm-color-border)',
              opacity: tier.enabled ? 1 : 0.55,
            }}
          >
            <span style={{ flex: '1 1 10rem', fontWeight: 600 }}>
              {tier.name} · {tier.monthlyCoins}
              {tier.perks ? ` · ${tier.perks}` : ''}
            </span>
            <label style={{ display: 'inline-flex', gap: '0.35rem', alignItems: 'center', fontSize: '0.8125rem' }}>
              <input
                type="checkbox"
                checked={tier.enabled}
                disabled={isSaving}
                onChange={() => {
                  void handleToggleEnabled(tier);
                }}
              />
              {t('common.studio.fanClubTiers.enabled')}
            </label>
            <Button
              label={t('common.studio.fanClubTiers.delete')}
              variant="ghost"
              disabled={isSaving}
              onClick={() => {
                void handleDelete(tier.id);
              }}
            />
          </li>
        ))}
      </ul>

      {tiers.length === 0 ? (
        <p style={{ color: 'var(--fm-color-text-muted)', fontSize: '0.875rem' }}>
          {t('common.studio.fanClubTiers.empty')}
        </p>
      ) : null}

      <div style={{ display: 'grid', gap: '0.5rem', marginTop: '0.75rem' }}>
        <label>
          {t('common.studio.fanClubTiers.name')}
          <input
            className="fm-input"
            value={draft.name}
            onChange={(event) => {
              setDraft({ ...draft, name: event.target.value });
            }}
            style={{ display: 'block', width: '100%', marginTop: '0.25rem' }}
          />
        </label>
        <label>
          {t('common.studio.fanClubTiers.monthlyCoins')}
          <input
            className="fm-input"
            type="number"
            min={1}
            value={draft.monthlyCoins}
            onChange={(event) => {
              setDraft({ ...draft, monthlyCoins: event.target.value });
            }}
            style={{ display: 'block', width: '100%', marginTop: '0.25rem' }}
          />
        </label>
        <label>
          {t('common.studio.fanClubTiers.perks')}
          <input
            className="fm-input"
            value={draft.perks}
            onChange={(event) => {
              setDraft({ ...draft, perks: event.target.value });
            }}
            placeholder={t('common.studio.fanClubTiers.perksPlaceholder')}
            style={{ display: 'block', width: '100%', marginTop: '0.25rem' }}
          />
        </label>
        <Button
          label={isSaving ? t('common.studio.fanClubTiers.saving') : t('common.studio.fanClubTiers.add')}
          disabled={isSaving}
          onClick={() => {
            void handleAdd();
          }}
        />
      </div>
    </div>
  );
}
